/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-debugger */
import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import axios from 'axios';
import { environment } from '@/environments/environment';
import { ProjectsResponse, ProjectsResponseDetails } from '@/app/interfaces/projects';
import { ProjectCorrectionPayload } from '@/app/interfaces/project';
import {
  AuroraPresignedReportUrlRequest,
  AuroraPresignedReportUrlResponse,
  AuroraReportDataRequest,
  AuroraReportDataResponse,
  SendSpdfReportPayload,
} from '@/app/interfaces/report-spdf';
import { ToastService } from '../script/toast.service';
import { LoaderService } from '@/app/services/interceptor/loader-service';
import { PermitSynchronizeService } from './permit-synchronize.service';

type ProjectCreationPayload = Record<string, unknown> & {
  jobcode?: string;
  contactEmailResponsible?: string;
  selectedOrgCode?: string;
  csvEipd?: string | null;
};
@Injectable({
  providedIn: 'root',
})
export class ProjectsService {
  constructor(
    @Inject(DOCUMENT) private document: Document,
    private toastService: ToastService,
    private loader: LoaderService,
    private permitSynchronizeService: PermitSynchronizeService
  ) {}

  async getProjectsList(filters): Promise<ProjectsResponse> {
    try {
      this.loader.show();
      const response = await axios.post<ProjectsResponse>(
        `${environment.url}/api/aurora/listado`,
        // { filters, jobcode },
        { filters, list: 'projects' },
        {
          withCredentials: true,
        }
      );

      return response.data;
    } catch (error) {
      this.toastService.showError(
        'Error al cargar el listado de proyectos',
        error?.response?.data?.message || 'Ha ocurrido un error inesperado'
      );
      throw error;
    } finally {
      this.loader.hide();
    }
  }
  async getProjectById(id: string, role: string, detail?: string): Promise<ProjectsResponseDetails> {
    try {
      this.loader.show();
      const payload = role ? { id_proyecto: id, role, detail } : { id_proyecto: id, detail };
      const { data } = await axios.post<ProjectsResponseDetails>(`${environment.url}/api/aurora/detalle`, payload, {
        withCredentials: true,
      });
      return data;
    } catch (error) {
      this.toastService.showError(
        'Error al cargar el proyecto',
        error?.response?.data?.message || 'Ha ocurrido un error inesperado'
      );
      throw error;
    } finally {
      this.loader.hide();
    }
  }

  async getReportData(request: AuroraReportDataRequest): Promise<AuroraReportDataResponse> {
    try {
      this.loader.show();

      const { data } = await axios.post<AuroraReportDataResponse>(
        `${environment.url}/api/aurora/obtener_datos_informe`,
        request,
        {
          withCredentials: true,
        }
      );

      return data;
    } catch (error) {
      this.toastService.showError(
        'Error al obtener los datos del informe',
        error?.response?.data?.message || 'Ha ocurrido un error inesperado'
      );
      throw error;
    } finally {
      this.loader.hide();
    }
  }

  async generatePresignedReportUrl(projectCode: number): Promise<AuroraPresignedReportUrlResponse> {
    const request: AuroraPresignedReportUrlRequest = {
      solution: 'REGSIA',
      type: 'informe_fase3',
      projectCode,
      contentType: 'application/pdf',
    };

    try {
      this.loader.show();

      const { data } = await axios.post<AuroraPresignedReportUrlResponse>(
        `${environment.url}/api/aurora/generar_url_prefirmada`,
        request,
        {
          withCredentials: true,
        }
      );

      if (!data?.uploadUrl || !data?.s3Key) {
        throw new Error('No se pudo obtener una URL prefirmada válida');
      }

      return data;
    } catch (error) {
      this.toastService.showError(
        'Error al generar la URL prefirmada',
        error?.response?.data?.message || error?.message || 'Ha ocurrido un error inesperado'
      );
      throw error;
    } finally {
      this.loader.hide();
    }
  }

  async createNewProject(body: ProjectCreationPayload, creatorOrgCode?: string): Promise<ProjectsResponse> {
    try {
      this.loader.show();
      const response = await axios.post<ProjectsResponse>(
        `${environment.url}/api/aurora/validar_y_guardar_proyecto_IA`,
        body,
        {
          withCredentials: true,
        }
      );
      this.permitSynchronizeService.synchronizePermissions(
        creatorOrgCode,
        body?.jobcode,
        body?.contactEmailResponsible,
        undefined,
        body?.selectedOrgCode,
        true
      );
      this.toastService.showSuccess('Proyecto creado', 'El proyecto se ha creado correctamente');
      return response.data;
    } catch (error) {
      this.toastService.showError(
        'Error al crear proyecto.',
        error?.response?.data?.message || 'Ha ocurrido un error inesperado'
      );
      throw error;
    } finally {
      this.loader.hide();
    }
  }

  async submitProjectCorrection(body: ProjectCorrectionPayload): Promise<any> {
    try {
      this.loader.show();

      const { data } = await axios.post(`${environment.url}/api/aurora/subsanar_proyecto_IA`, body, {
        withCredentials: true,
      });

      this.toastService.showSuccess('Subsanación enviada', 'La subsanación se ha enviado correctamente');

      return data;
    } catch (error) {
      this.toastService.showError(
        'Error al enviar la subsanación',
        error?.response?.data?.message || 'Ha ocurrido un error inesperado'
      );
      throw error;
    } finally {
      this.loader.hide();
    }
  }

  async changeProjectState(payload: {
    userRole: string;
    state: string;
    idProyecto: string;
    nombreProyecto: string;
    comentario: string;
    camposSeleccionados: string[];
  }): Promise<any> {
    try {
      this.loader.show();

      const { data } = await axios.post(`${environment.url}/api/aurora/cambio_estado_proyecto`, payload, {
        withCredentials: true,
      });

      this.toastService.showSuccess(
        'Revisión enviada correctamente',
        'La revisión del proyecto se ha enviado con éxito'
      );

      return data;
    } catch (error) {
      this.toastService.showError(
        'Error al enviar la revisión',
        error?.response?.data?.message || 'Ha ocurrido un error inesperado'
      );
      throw error;
    } finally {
      this.loader.hide();
    }
  }

  async sendGeneratedReport(payload: SendSpdfReportPayload): Promise<any> {
    try {
      this.loader.show();

      const { data } = await axios.post(`${environment.url}/api/aurora/cambio_estado_proyecto`, payload, {
        withCredentials: true,
      });

      this.toastService.showSuccess('Informe enviado correctamente', 'El informe se ha enviado con éxito');

      return data;
    } catch (error) {
      this.toastService.showError(
        'Error al enviar el informe',
        error?.response?.data?.message || 'Ha ocurrido un error inesperado'
      );
      throw error;
    } finally {
      this.loader.hide();
    }
  }
}
